import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from 'lucide-react';
import { formatPrice, upsells } from '../data/upsells';
import { OFFER_PRICE, VARIANT_IDS } from '../utils/metaPixel';
type UpsellModalProps = {
  open: boolean;
  onClose: () => void;
};
const UPSELL_PRICE = {
  braveMind: 89.0,
  littleEngineer: 27.99,
  focusLab: 19.99
} as const;
export const UPSELL_IDS = {
  braveMind: 'upsell-brave-mind',
  littleEngineer: 'upsell-little-engineer',
  focusLab: 'upsell-focus-lab'
} as const;
const FOCUSABLE = 'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
function lockBodyScroll() {
  const y = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${y}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
  document.body.dataset.scrollY = String(y);
}
function unlockBodyScroll() {
  const y = Number(document.body.dataset.scrollY || 0);
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  delete document.body.dataset.scrollY;
  window.scrollTo(0, y);
}
function isChecked(elementId: string): boolean {
  const input = document.getElementById(elementId) as HTMLInputElement | null;
  return input?.checked === true;
}
function trackInitiateCheckout(value: number, contentIds: string[]) {
  window.fbq?.('track', 'InitiateCheckout', {
    value,
    currency: 'USD',
    content_name: 'Calm Mornings Mega Kit',
    content_type: 'product',
    content_ids: contentIds,
    num_items: contentIds.length
  });
}

/** The kit on its own — decline link, scrim click, Escape. */
function goToKitOnly() {
  const url = 'https://myeasyguide.online/cart/49960131133682:1';
  trackInitiateCheckout(OFFER_PRICE, [VARIANT_IDS.kit]);
  console.log('GOING TO:', url);
  window.location.href = url;
}

/** Every case written out by name. Each URL is a complete literal string. */
function goToCheckout() {
  const braveMind = isChecked(UPSELL_IDS.braveMind);
  const littleEngineer = isChecked(UPSELL_IDS.littleEngineer);
  const focusLab = isChecked(UPSELL_IDS.focusLab);
  let url = '';
  if (braveMind && littleEngineer && focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49930211688690:1,49902595670258:1,49945662128370:1';else if (braveMind && littleEngineer && !focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49930211688690:1,49902595670258:1';else if (braveMind && !littleEngineer && focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49930211688690:1,49945662128370:1';else if (braveMind && !littleEngineer && !focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49930211688690:1';else if (!braveMind && littleEngineer && focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49902595670258:1,49945662128370:1';else if (!braveMind && littleEngineer && !focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49902595670258:1';else if (!braveMind && !littleEngineer && focusLab) url = 'https://myeasyguide.online/cart/49960131133682:1,49945662128370:1';else url = 'https://myeasyguide.online/cart/49960131133682:1';
  const value = Math.round((OFFER_PRICE + (braveMind ? UPSELL_PRICE.braveMind : 0) + (littleEngineer ? UPSELL_PRICE.littleEngineer : 0) + (focusLab ? UPSELL_PRICE.focusLab : 0)) * 100) / 100;
  const contentIds = [VARIANT_IDS.kit];
  if (braveMind) contentIds.push(VARIANT_IDS.braveMind);
  if (littleEngineer) contentIds.push(VARIANT_IDS.littleEngineer);
  if (focusLab) contentIds.push(VARIANT_IDS.focusLab);
  trackInitiateCheckout(value, contentIds);
  console.log('GOING TO:', url);
  window.location.href = url;
}
export function UpsellModal({
  open,
  onClose
}: UpsellModalProps) {
  const [selected, setSelected] = useState<Array<keyof typeof UPSELL_PRICE>>([]);
  const [shown, setShown] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) {
      setShown(false);
      setSelected([]);
      return;
    }
    lockBodyScroll();
    const raf = window.requestAnimationFrame(() => setShown(true));
    const focusTimer = window.setTimeout(() => {
      sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        goToKitOnly();
        return;
      }
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const nodes = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(node => !node.hasAttribute('disabled'));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.cancelAnimationFrame(raf);
      window.clearTimeout(focusTimer);
      unlockBodyScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id: keyof typeof UPSELL_PRICE) => {
    setSelected(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  if (!open || typeof document === 'undefined') return null;
  const total = Math.round((OFFER_PRICE + selected.reduce((sum, id) => sum + UPSELL_PRICE[id], 0)) * 100) / 100;
  return createPortal(<div className="upsell-modal" data-id="element-199" data-mp="33wm1k3coi02d">
      <div aria-hidden="true" onClick={goToKitOnly} className="fixed inset-0 transition-opacity duration-200 ease-out" style={{
      zIndex: 9998,
      backgroundColor: 'rgba(20,17,15,.55)',
      opacity: shown ? 1 : 0
    }} data-id="element-200" data-mp="33xngwk0q98pw" />

      <div ref={sheetRef} role="dialog" aria-modal="true" aria-label="Add to your kit before checkout" className={`fixed bottom-0 left-0 right-0 flex max-h-[85dvh] flex-col rounded-t-[24px] bg-bg transition-[transform,opacity] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-opacity md:bottom-auto md:left-1/2 md:right-auto md:top-1/2 md:w-full md:max-w-[540px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[20px] ${shown ? 'translate-y-0' : 'translate-y-full'}`} style={{
      zIndex: 9999,
      opacity: shown ? 1 : 0
    }} data-id="element-201" data-mp="1dlh6jfff5ie9">
        <div className="min-h-0 flex-1 overflow-y-auto px-[26px] pb-4 pt-[26px]" style={{
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-202" data-mp="25sh8hep07aa">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-star" data-id="element-203" data-mp="28uubqtuvgcjr">
            One-time offer — this page only
          </p>

          <h2 className="mt-2 text-left text-[22px] font-extrabold text-ink" data-id="element-204" data-mp="2017eezb0n18g">
            Add anything below and it ships with your kit.
          </h2>

          <div className="mt-5 space-y-2.5" data-id="element-205" data-mp="241hu53om160i">
            {upsells.map(row => {
            const checked = selected.includes(row.id);
            return <label key={row.id} className="flex cursor-pointer items-start gap-3 rounded-[12px] p-3.5 transition-colors duration-150 ease-out" style={{
              border: checked ? '1px solid #12A05A' : '1px dashed rgba(20,17,15,0.30)'
            }} data-id="element-206" data-mp="1v2nt9148xth5">
                  <input id={UPSELL_IDS[row.id]} type="checkbox" checked={checked} onChange={() => toggle(row.id)} className="sr-only" data-id="element-207" data-mp="3bgw2xv3x48bd" />
                  <span aria-hidden="true" className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-[6px]" style={{
                border: checked ? '2px solid #12A05A' : '2px solid rgba(20,17,15,0.35)'
              }} data-id="element-208" data-mp="1y8zw8pmv4rkp">
                    {checked ? <CheckIcon className="h-4 w-4 text-[#12A05A]" strokeWidth={3} data-id="element-209" data-mp="1kfs4mxys7w30" /> : null}
                  </span>

                  {row.image ? <img src={row.image} alt="" aria-hidden="true" loading="lazy" decoding="async" className="h-14 w-14 flex-none self-start rounded-[8px] object-cover" style={{
                border: '1px solid rgba(20,17,15,.12)'
              }} data-id="element-210" data-mp="1akgvqwa21j36" /> : <span aria-hidden="true" className="flex h-14 w-14 flex-none items-center justify-center self-start rounded-[8px] bg-[#dcdcdc] p-1 text-center text-[7px] font-medium uppercase leading-tight tracking-[0.08em] text-[#5a5a5a]" style={{
                border: '1px solid rgba(20,17,15,.12)'
              }} data-id="element-211" data-mp="38e2ycx91hj9p">
                      {row.thumbLabel}
                    </span>}

                  <span className="min-w-0 flex-1 text-left" data-id="element-212" data-mp="2tj54u125mnr1">
                    <span className="block text-[15px] font-extrabold leading-[1.3]" data-id="element-213" data-mp="2yh6lkfc3yazc">
                      {row.title}
                    </span>

                    <span className="mt-1 flex items-baseline gap-2" data-id="element-214" data-mp="33i43cqt7h30b">
                      <span className="text-[16px] font-extrabold" data-id="element-215" data-mp="dqfn39gboudb">{formatPrice(row.price)}</span>
                      {row.compareAt ? <span className="text-[13px] text-ink/45 line-through" data-id="element-216" data-mp="isogg6x3xyfy">
                          {formatPrice(row.compareAt)}
                        </span> : null}
                    </span>

                    <span className="mt-1.5 block text-[13px] italic leading-[1.45] text-ink/70" data-id="element-217" data-mp="29m3e7j2on1l4">
                      {row.description}
                    </span>
                  </span>
                </label>;
          })}
          </div>
        </div>

        <div className="relative flex-none bg-bg px-5 pt-4" style={{
        borderTop: '1px solid rgba(20,17,15,.12)',
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))'
      }} data-id="element-218" data-mp="1gz8wuv15wibv">
          <div aria-hidden="true" className="pointer-events-none absolute -top-6 left-0 right-0 h-6" style={{
          background: 'linear-gradient(to bottom, rgba(246,239,230,0), #F6EFE6)'
        }} data-id="element-219" data-mp="1uxgnymbiopf4" />

          <p className="text-center text-[18px] font-extrabold" aria-live="polite" data-id="element-220" data-mp="36e70soag4fhp">
            Total: ${total.toFixed(2)}
          </p>

          <button type="button" onClick={goToCheckout} className="mt-3 h-[52px] w-full rounded-[999px] bg-[#12A05A] text-[16px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-[#0E8C4E] active:translate-y-[1px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink" data-id="element-221" data-mp="3bgfwi4sxza83">
            Continue to checkout →
          </button>

          <button type="button" onClick={goToKitOnly} className="mx-auto mt-3 block text-[14px] text-ink/60 underline underline-offset-4" data-id="element-222" data-mp="2ssj8qy3pfaop">
            No thanks, just my kit
          </button>
        </div>
      </div>
    </div>, document.body);
}